import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getCategoryTabs } from "store/HomeSlice";
import ProductsWithSlide from "components/ProductsWithSlide";
import langData from "lang/data";
import styles from "./style.module.css";

// Mirrors the price shown by ProductCard so the tab order matches the cards.
function getDisplayedPrice(item) {
  const product = item?.product;
  const pricing = item?.pricing;

  const hasPromo =
    item?.promo_price ||
    (product?.online_price && product?.online_selling_price) ||
    pricing?.promo_price ||
    (pricing?.is_online && pricing?.online_selling_price);

  let base;
  if (!hasPromo) {
    base =
      product?.average_price ||
      pricing?.average_price ||
      item?.skus?.[0]?.selling_price ||
      pricing?.selling_price;
  } else if (item?.promo_price) {
    base = item.promo_price;
  } else if (product?.online_price && product?.online_selling_price) {
    base = product.online_selling_price;
  } else if (pricing?.promo_price) {
    base = pricing.promo_price;
  } else if (pricing?.is_online && pricing?.online_selling_price) {
    base = pricing.online_selling_price;
  } else {
    base = item?.skus?.[0]?.selling_price;
  }

  return (+base || 0) - (+product?.installing_price || 0);
}

export default function CategoryTabsProducts() {
  const dispatch = useDispatch();
  const currentLanguage = useSelector(({ main }) => main.currentLanguage);
  const { categoryTabs } = useSelector(({ home }) => home);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const tabsWrapRef = useRef(null);

  const selectCategory = (category, element) => {
    setActiveCategoryId(category.id);

    const wrap = tabsWrapRef.current;
    if (!wrap || !element) return;

    const left = element.offsetLeft - (wrap.clientWidth - element.offsetWidth) / 2;
    wrap.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  };

  useEffect(() => {
    if (!categoryTabs?.categories?.length) {
      dispatch(getCategoryTabs());
    }
  }, [categoryTabs?.categories?.length, dispatch]);

  useEffect(() => {
    if (!activeCategoryId && categoryTabs?.categories?.length) {
      setActiveCategoryId(categoryTabs.categories[0].id);
    }
  }, [activeCategoryId, categoryTabs?.categories]);

  const title = useMemo(() => {
    if (!categoryTabs) return null;
    return (
      categoryTabs[`title_${currentLanguage}`] ||
      categoryTabs.title_hy ||
      categoryTabs.title_en ||
      null
    );
  }, [categoryTabs, currentLanguage]);

  const activeCategory = useMemo(() => {
    if (!activeCategoryId || !categoryTabs?.categories?.length) return null;
    return (
      categoryTabs.categories.find(
        (category) => Number(category.id) === Number(activeCategoryId)
      ) || null
    );
  }, [activeCategoryId, categoryTabs?.categories]);

  const products = useMemo(() => {
    if (!activeCategoryId || !categoryTabs?.products_by_category) return [];
    const list =
      categoryTabs.products_by_category[String(activeCategoryId)] ||
      categoryTabs.products_by_category[activeCategoryId] ||
      [];

    return [...list].sort((a, b) => {
      const priceA = getDisplayedPrice(a);
      const priceB = getDisplayedPrice(b);
      // Products without a usable price stay at the end instead of leading the row.
      return (priceA > 0 ? priceA : Infinity) - (priceB > 0 ? priceB : Infinity);
    });
  }, [activeCategoryId, categoryTabs?.products_by_category]);

  if (!categoryTabs?.categories?.length) {
    return null;
  }

  const langPrefix = currentLanguage !== "hy" ? `/${currentLanguage}` : "";

  return (
    <div className={`wrapper ${styles.section}`}>
      {title ? <h3 className={styles.title}>{title}</h3> : null}
      <div className={styles.tabsWrap} ref={tabsWrapRef}>
        <div className={styles.tabs}>
          {categoryTabs.categories.map((category) => {
            const isActive = Number(activeCategoryId) === Number(category.id);
            const label =
              category[`name_${currentLanguage}`] ||
              category.name_hy ||
              category.name_en ||
              "";
            return (
              <button
                key={category.id}
                type="button"
                className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                onClick={(event) => selectCategory(category, event.currentTarget)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {products?.length ? (
        <ProductsWithSlide
          key={activeCategoryId}
          productsData={products}
          noWrapper
          item_list_id={`category-tabs-${activeCategoryId}`}
          item_list_name={title || "Category tabs"}
        />
      ) : null}
      {activeCategory?.slug ? (
        <div className={styles.footer}>
          <Link
            className={styles.viewAllBtn}
            to={`${langPrefix}/category/filter/${activeCategory.slug}`}
          >
            {langData.buttons.view_all[currentLanguage]}
          </Link>
        </div>
      ) : null}
    </div>
  );
}
